import React, { useRef, useState } from 'react';
import { Download, ShieldCheck, X } from 'lucide-react';
import Dialog from '../common/Dialog';
import { policies, policyCopy, policyLanguages, policyPdfUrl, initialPolicyLanguage, savePolicyLanguage } from '../../legal/policy';
import '../../styles/legal-policy.css';

export function PolicyLanguagePicker({ language, onChange }) {
  return <div className="legal-language-picker" role="group" aria-label={policyCopy[language].language}>
    {policyLanguages.map(([value, label]) => <button type="button" key={value} lang={value} className="ap-button"
      aria-pressed={language === value} onClick={() => onChange(value)}>{label}</button>)}
  </div>;
}

export function PolicyDownload({ language }) {
  return <a className="ap-button legal-download" href={policyPdfUrl} download="Audit-Pro-personal-data-policy-ru.pdf" target="_blank" rel="noopener noreferrer">
    <Download size={16} aria-hidden="true" />{policyCopy[language].original}
  </a>;
}

function Block({ block, copy }) {
  if (block.type === 'ul') return <ul>{block.items.map((text, i) => <li key={i}>{text}</li>)}</ul>;
  if (block.type === 'definitions') return <dl className="legal-definitions">{block.items.map((item, i) => <div key={i}>
    <dt>{i + 1}. {item.term}</dt><dd>{item.text}</dd>
  </div>)}</dl>;
  if (block.type === 'table') return <div className="legal-table-scroll" role="region" aria-label={copy.table} tabIndex={0}>
    <table className="legal-table"><thead><tr>{block.columns.map((text, i) => <th key={i} scope="col">{text}</th>)}</tr></thead>
      <tbody>{block.rows.map((row, i) => <tr key={i}>{row.map((text, j) => j === 0
        ? <th key={j} scope="row">{text}</th> : <td key={j}>{text}</td>)}</tr>)}</tbody>
    </table>
  </div>;
  return <p>{block.text}</p>;
}

export default function PolicyReader({ language, modal = false }) {
  const doc = policies[language] || policies.ru, copy = policyCopy[doc.language];
  const root = useRef(null);
  const Title = modal ? 'h2' : 'h1';
  const SectionTitle = modal ? 'h3' : 'h2';
  const jump = id => {
    const heading = root.current.querySelector(`[data-policy-section="${id}"]`);
    heading?.scrollIntoView({ block: 'start', behavior: 'auto' });
    heading?.focus({ preventScroll: true });
  };
  return <article ref={root} className="legal-document" lang={doc.language} translate="no">
    <Title className="legal-document-title">{doc.title}</Title>
    <p className="legal-source-note">{copy.source}</p>
    {doc.language !== 'ru' && <p className="legal-notice" role="note">{copy.translation}</p>}
    <details className="legal-metadata"><summary>{copy.metadata}</summary>
      <p><strong>{doc.operator}</strong><br />{doc.address}<br /><a href={`mailto:${doc.email}`}>{doc.email}</a></p>
      <ul>{doc.metadata.map(text => <li key={text}>{text}</li>)}</ul>
    </details>
    <details className="legal-toc"><summary>{copy.contents}</summary><nav aria-label={copy.contents}>
      {doc.sections.map(section => <button type="button" key={section.id} onClick={() => jump(section.id)}>{section.title}</button>)}
    </nav></details>
    {doc.sections.map(section => <section key={section.id} className="legal-section">
      {section.id === 'appendix-1' && <p className="legal-notice legal-template-notice" role="note">{copy.appendix}</p>}
      <SectionTitle data-policy-section={section.id} tabIndex={-1}>{section.title}</SectionTitle>
      {section.blocks.map((block, i) => <Block key={i} block={block} copy={copy} />)}
    </section>)}
  </article>;
}

export function PolicyDialog({ onClose }) {
  const [language, setLanguage] = useState(() => initialPolicyLanguage());
  const body = useRef(null), copy = policyCopy[language];
  const changeLanguage = value => {
    setLanguage(value); savePolicyLanguage(value);
    if (body.current) body.current.scrollTop = 0;
  };
  return <Dialog size="lg" className="legal-policy-dialog" onClose={onClose} labelledBy="legal-dialog-title" lang={language}>
    <header className="ap-dialog__header">
      <h2 id="legal-dialog-title" data-dialog-heading tabIndex={-1} className="ap-dialog__title"><ShieldCheck size={21} aria-hidden="true" />{copy.heading}</h2>
      <button type="button" className="ap-dialog__close" aria-label={copy.close} onClick={onClose}><X size={19} aria-hidden="true" /></button>
    </header>
    <div className="legal-dialog-toolbar"><PolicyLanguagePicker language={language} onChange={changeLanguage} /></div>
    <div ref={body} className="ap-dialog__body"><PolicyReader language={language} modal /></div>
    <footer className="ap-dialog__footer"><PolicyDownload language={language} /><button type="button" className="ap-button" onClick={onClose}>{copy.close}</button></footer>
  </Dialog>;
}
